import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import * as Sentry from '@sentry/react'
import App from './App'
import ErrorBoundary from './components/ErrorBoundary'
import { analytics, initAnalytics } from './lib/analytics'
import { captureAttribution } from './lib/attribution'
import './index.css'

captureAttribution()
initAnalytics()
// Stripe sends a cancelled checkout back with ?checkout=cancelled (billing.py).
if (new URLSearchParams(window.location.search).get('checkout') === 'cancelled') {
  analytics.track('checkout_abandoned')
}

// Initialize before render — catches errors during hydration too. No-ops
// cleanly if VITE_SENTRY_DSN isn't set (local dev, or before the Sentry
// project exists). Pattern matches WitPit's existing Sentry setup.
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_GIT_SHA,
    tracesSampleRate: 0.1,
    integrations: [Sentry.browserTracingIntegration()],
    ignoreErrors: [
      // Expected auth/rate-limit responses — not bugs
      '401',
      '429',
      // Browser noise
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Safari quirks
      'Non-Error promise rejection captured',
    ],
  })
}

// Apply theme before render to avoid flash
const stored = localStorage.getItem('ls-theme')
const preferDark = stored
  ? stored === 'dark'
  : window.matchMedia('(prefers-color-scheme: dark)').matches
if (preferDark) document.documentElement.classList.add('dark')

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <HelmetProvider>
      <ErrorBoundary>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </ErrorBoundary>
    </HelmetProvider>
  </React.StrictMode>,
)
